import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { SpinnerGap, Copy, Check, ArrowSquareOut, LinkSimple } from "@phosphor-icons/react";

export function ApprovalLinkModal({ open, onOpenChange, historyItemIds = [], defaultTitle = "" }) {
  const { api } = useAuth();
  const [title, setTitle] = useState(defaultTitle || "Content Review");
  const [message, setMessage] = useState("");
  const [expiresIn, setExpiresIn] = useState("14");
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleCreate = async () => {
    if (!historyItemIds.length) return;
    setCreating(true);
    try {
      const resp = await api.post("/approvals/create-link", {
        history_item_ids: historyItemIds,
        title,
        message_to_client: message.trim() || null,
        expires_in_days: expiresIn === "none" ? null : parseInt(expiresIn),
      });
      setResult(resp.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create link");
    }
    setCreating(false);
  };

  const handleCopy = () => {
    if (result?.review_url) {
      navigator.clipboard.writeText(result.review_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setResult(null);
    setCopied(false);
    setTitle(defaultTitle || "Content Review");
    setMessage("");
    setExpiresIn("14");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[var(--bg-raised)] border-[var(--border-strong)] text-[var(--text-primary)] max-w-md" data-testid="approval-link-modal">
        <DialogHeader>
          <DialogTitle className="text-[var(--text-primary)] flex items-center gap-2">
            <LinkSimple size={18} className="text-[#FF4400]" />
            {result ? "Link Created!" : "Share for Client Approval"}
          </DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <div>
              <label className="text-xs text-[var(--text-muted)] mb-1 block">Review Title</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} className="bg-[var(--bg-base)] border-[var(--border-strong)] text-sm" data-testid="approval-title-input" />
            </div>
            <div>
              <label className="text-xs text-[var(--text-muted)] mb-1 block">Message to Client <span className="text-[var(--text-muted)]">(optional)</span></label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Hi, here's the content for your review..." rows={2} className="bg-[var(--bg-base)] border-[var(--border-strong)] text-sm resize-y" data-testid="approval-message-input" />
            </div>
            <div>
              <label className="text-xs text-[var(--text-muted)] mb-1 block">Link Expires In</label>
              <Select value={expiresIn} onValueChange={setExpiresIn}>
                <SelectTrigger className="bg-[var(--bg-base)] border-[var(--border-strong)] text-sm" data-testid="approval-expiry-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[var(--bg-card)] border-[var(--border-strong)]">
                  <SelectItem value="none">Never</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-[10px] text-[var(--text-muted)]">{historyItemIds.length} item{historyItemIds.length !== 1 ? "s" : ""} will be shared for review</p>
            <Button onClick={handleCreate} disabled={creating || !title.trim()} className="w-full bg-[#FF4400] hover:bg-[#FF6633] text-white" data-testid="create-link-btn">
              {creating ? <SpinnerGap size={16} className="animate-spin mr-1.5" /> : null}
              Create Link
            </Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="approval-link-success">
            <div className="bg-[var(--bg-base)] border border-[var(--border)] rounded-lg p-3">
              <p className="text-xs text-[var(--text-muted)] mb-1">Shareable Link</p>
              <p className="text-sm text-[var(--text-primary)] font-mono break-all select-all" data-testid="approval-link-url">{result.review_url}</p>
            </div>
            <Button onClick={handleCopy} className={`w-full ${copied ? "bg-emerald-500 hover:bg-emerald-600" : "bg-[#FF4400] hover:bg-[#FF6633]"} text-white`} data-testid="copy-link-btn">
              {copied ? <><Check size={16} weight="bold" className="mr-1.5" /> Copied!</> : <><Copy size={16} className="mr-1.5" /> Copy Link</>}
            </Button>
            <div className="flex items-center justify-between">
              <a href={result.review_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#FF4400] hover:text-[#FF6633] flex items-center gap-1 transition-colors" data-testid="open-preview-link">
                <ArrowSquareOut size={12} /> Open Preview
              </a>
              <Button variant="ghost" onClick={handleClose} className="text-sm text-[var(--text-secondary)]" data-testid="done-btn">Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
